import { HorizontalLinearStepper } from '@common/components/Stepper'
import { useStepper } from '@common/context/StepperContext'
import { ArrowBackRounded } from '@mui/icons-material'
import { Box, Toolbar, AppBar, Button, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ExamReassignementEsignFormValues,
  ExamReassignementEsignValidationSchema,
  ExamReassignementFormValues,
} from '@features/qc_task_details_view/schemas/ExamReassignementEsignValidationSchema'
import EsignModal from '../modals/EsignModal'
import { useTimepointExamReassignementEsignMutation } from '@features/qc_task_details_view/hooks/useTimepointExamReassignementEsignMutation'
import { useParams } from 'react-router-dom'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useAuth } from 'react-oidc-context'
import { storeSigningIntent } from '@auth/utils/storeSigningIntent'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'

type ReassignExamFooterProps = {
  handleContinue: () => void
  isSubmitting: boolean
  setIsSubmitting: (state: boolean) => void
  handleClose: () => void
}

function ReassignExamFooter({ handleContinue, isSubmitting, setIsSubmitting, handleClose }: ReassignExamFooterProps) {
  const { steps, activeStep, setActiveStep, setSteps } = useStepper()
  const [isEsignModalOpen, setIsEsignModalOpen] = useState(false)
  const { user } = useExtendedAuth()
  const { signinRedirect } = useAuth()
  const { studyId, subjectId, timepointSubmissionId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId: string
  }>()

  // Get access to the main wizard form context
  const {
    getValues,
    formState: { isValid },
  } = useFormContext<ExamReassignementFormValues>()

  // E-sign form setup
  const esignMethods = useForm<ExamReassignementEsignFormValues>({
    resolver: zodResolver(ExamReassignementEsignValidationSchema),
    defaultValues: {
      username: user?.profile.preferred_username || '',
      context: {
        type: 'timepoint_exam_reassignment',
        exam_reassignment: {
          exam_metadata_ids: [],
          from_timepoint_submission: {},
          to_timepoint_submission: {
            timepoint_submission_id: '',
          },
        },
      },
    },
  })

  // Set up mutation for ESign submission
  const examReassignmentMutation = useTimepointExamReassignementEsignMutation()

  const { isSigningInProgress } = useRestoreSigningProcess(examReassignmentMutation, {
    successMessage: 'Exam reassigned successfully',
    errorMessage: 'Failed to complete exam reassignment',
    contextType: 'timepoint_exam_reassignment',
    onSuccess: () => {
      setIsEsignModalOpen(false)
      handleClose()
    },
    onError: () => {
      setIsEsignModalOpen(false)
      handleClose()
    },
  })

  useEffect(() => {
    const reassignModalState = sessionStorage.getItem('reassign_exam_modal_state')

    if (reassignModalState) {
      try {
        const modalState = JSON.parse(reassignModalState)
        if (modalState.isEsignModalOpen) {
          setIsEsignModalOpen(true)
          setActiveStep(steps.length - 1)

          if (modalState.formValues) {
            Object.keys(modalState.formValues).forEach((key) => {
              esignMethods.setValue(
                `context.exam_reassignment.${key}` as keyof ExamReassignementEsignFormValues,
                modalState.formValues[key],
              )
            })
          }
        }
      } catch (error) {
        console.error('Error restoring reassign exam modal state:', error)
      }
    }
  }, [])

  const handleStepClick = (stepIndex: number) => {
    // Only allow going back to previous steps
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex)
    }
  }

  const handlePreviousStep = () => {
    if (activeStep === 0) {
      handleClose()
    } else {
      setActiveStep(activeStep - 1)
    }
  }

  const openEsignModal = () => {
    if (activeStep === steps.length - 1) {
      // Get current form values to use in the e-sign modal
      const currentValues = getValues()

      // Prepare form data with the current selections for the e-sign modal
      esignMethods.setValue('context.exam_reassignment', {
        exam_metadata_ids: currentValues.exam_metadata_ids,
        from_timepoint_submission: {
          new_status: currentValues.from_timepoint_submission.new_status,
        },
        to_timepoint_submission: {
          timepoint_submission_id: currentValues.to_timepoint_submission.timepoint_submission_id,
          new_status: currentValues.to_timepoint_submission.new_status,
        },
      })

      setIsEsignModalOpen(true)
    } else {
      handleContinue()
    }
  }

  const handleAuthRedirect = async () => {
    if (!studyId || !subjectId || !timepointSubmissionId) return

    setIsSubmitting(true)

    const currentValues = getValues()

    const payload = {
      type: 'timepoint_exam_reassignment',
      exam_reassignment: {
        exam_metadata_ids: currentValues.exam_metadata_ids,
        from_timepoint_submission: {
          new_status: currentValues.from_timepoint_submission.new_status,
        },
        to_timepoint_submission: {
          timepoint_submission_id: currentValues.to_timepoint_submission.timepoint_submission_id,
          new_status: currentValues.to_timepoint_submission.new_status,
        },
      },
    }

    sessionStorage.setItem(
      'reassign_exam_modal_state',
      JSON.stringify({
        isEsignModalOpen: true,
        formValues: {
          exam_metadata_ids: currentValues.exam_metadata_ids,
          from_timepoint_submission: {
            new_status: currentValues.from_timepoint_submission.new_status,
          },
          to_timepoint_submission: {
            timepoint_submission_id: currentValues.to_timepoint_submission.timepoint_submission_id,
            new_status: currentValues.to_timepoint_submission.new_status,
          },
        },
      }),
    )

    storeSigningIntent(payload, {
      pathParams: {
        study_id: studyId,
        subject_id: subjectId,
        timepoint_submission_id: timepointSubmissionId,
      },
      signer_name: getSignerName(),
    })

    await signinRedirect({
      prompt: 'login',
      max_age: 0,
      redirect_uri: window.location.href,
    })
  }

  const getSignerName = () => {
    const firstName = user?.profile.given_name || ''
    const lastName = user?.profile.family_name || ''
    let signerName = `${firstName} ${lastName}`.trim()

    if (!signerName) {
      signerName = user?.profile.email || user?.profile.preferred_username || ''
    }

    return signerName
  }

  // Check if user can continue to the next step based on form validation
  const canContinue = (): boolean => {
    const values = getValues()

    if (activeStep === 0) {
      // Need to have at least one exam selected
      return values.exam_metadata_ids.length > 0
    } else if (activeStep === 1) {
      // Need to have a timepoint submission selected
      return !!values.to_timepoint_submission?.timepoint_submission_id
    } else if (activeStep === 2) {
      // Need to have status selected for both timepoints
      return !!values.from_timepoint_submission?.new_status && !!values.to_timepoint_submission?.new_status
    }

    // For the review step, all conditions should be met
    return isValid
  }

  useEffect(() => {
    setSteps([
      { label: 'Select exams' },
      { label: 'Choose timepoint' },
      { label: 'Update submission status' },
      { label: 'Review and confirm' },
    ])
  }, [setSteps])

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          zIndex: 10,
          boxShadow: 2,
          width: '100%',
        }}
      >
        <AppBar position="static" sx={{ bgcolor: 'background.default', boxShadow: 2 }}>
          <Toolbar sx={{ justifyContent: 'space-between', columnGap: 8 }}>
            <Button
              size="large"
              color="primary"
              variant="text"
              startIcon={<ArrowBackRounded />}
              onClick={handlePreviousStep}
              disabled={isSubmitting || isSigningInProgress}
            >
              Previous step
            </Button>
            <Box sx={{ flex: 1 }}>
              <HorizontalLinearStepper steps={steps} activeStep={activeStep} onStepClick={handleStepClick} />
            </Box>
            <Button
              size="large"
              color="primary"
              variant="contained"
              onClick={openEsignModal}
              disabled={isSubmitting || isSigningInProgress || !canContinue()}
              startIcon={isSubmitting || isSigningInProgress ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting || isSigningInProgress
                ? 'Signing...'
                : activeStep === steps.length - 1
                  ? 'Confirm reassignment'
                  : 'Continue'}
            </Button>
          </Toolbar>
        </AppBar>
      </Box>

      <EsignModal
        open={isEsignModalOpen}
        onClose={() => setIsEsignModalOpen(false)}
        onSubmit={handleAuthRedirect}
        methods={esignMethods}
        isSubmitting={isSubmitting || isSigningInProgress}
      />
    </>
  )
}

export default ReassignExamFooter
